import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { getWpImageSrc } from '../../../lib/wpMedia';
import { isFinnishPage } from '../../../lib/blogPaths';

// Fallback when no logos from WP: static list (white logos; use brightness-0 on light bg).
const FALLBACK_LOGOS = [
  { name: 'Mehiläinen', slug: 'mehilainen', logoUrl: null },
  { name: 'Helsinki', slug: 'helsinki', logoUrl: null },
  { name: 'Elisa', slug: 'elisa', logoUrl: null },
  { name: 'Visma', slug: 'visma', logoUrl: null },
  { name: 'Smartum', slug: 'smartum', logoUrl: null },
  { name: 'Twoday', slug: 'twoday', logoUrl: null },
  { name: 'Lyyti', slug: 'lyyti', logoUrl: null },
  { name: 'VARHA', slug: 'varha', logoUrl: null },
];

/**
 * @param {{ logos?: Array<{ name: string, slug: string, logoUrl?: string|null }>, darkBackground?: boolean }} props
 * - logos: from WP (category "logos", ACF logoWhite). White logos; we adjust for light bg when darkBackground is false.
 * - darkBackground: when false (default), section is light — apply brightness-0 so white logos appear dark. When true, no filter.
 */
export function ServicesClientLogosSection({ logos = [], darkBackground = false }) {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const list = logos?.length > 0 ? [...logos].reverse() : [...FALLBACK_LOGOS].reverse();
  // White logos on light background: make them dark (brightness-0). On dark bg leave as-is.
  const imgClass = darkBackground
    ? 'max-w-full max-h-full object-contain'
    : 'max-w-full max-h-full object-contain brightness-0';
  const trustedByLabel = isFinnish ? 'Yhteistyökumppaneitamme' : 'Trusted by';

  return (
    <section ref={ref} className="section-rhythm bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.4 }}
          className="text-sm text-gray-500 mb-8 text-center"
        >
          {trustedByLabel}
        </motion.p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center justify-items-center">
          {list.map((logo, index) => (
            <motion.div
              key={logo.slug || logo.name || index}
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="h-20 flex items-center justify-center w-full max-w-[200px] p-2"
            >
              <div className="h-full w-full flex items-center justify-center opacity-70 hover:opacity-100 transition-[transform,opacity] duration-500 ease-out hover:scale-[1.04]">
                {logo.logoUrl ? (
                  <img
                    src={getWpImageSrc(logo.logoUrl) || logo.logoUrl}
                    alt={logo.name || 'Client logo'}
                    className={imgClass}
                  />
                ) : (
                  <img
                    src={`/logos/${logo.slug}.png`}
                    alt={logo.name}
                    className={imgClass}
                  />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
